<template>
  <z-form ref="form" class="z-card" @submit.native.prevent="submit">
    <h4>{{ $t('core.profile.block.user_info.title') }}</h4>

    <div class="z-card__body">
      <z-form-group class="row">
        <div class="col-3 text-right">
          {{ $t('core.profile.form.field.email.title') }}
        </div>
        <z-form-input class="col-9" vid="email">
          <i-input v-model="form.email" :disabled="true" />
        </z-form-input>
      </z-form-group>
      <div class="row z-form-item">
        <div class="col-3 text-right">{{ $t('payment.finance_email') }}</div>
        <div class="col-9">
          <i-select
            v-model="new_finance_emails"
            :multiple="true"
            :filterable="true"
            :allow-create="true"
            :disabled="is_finance_loading"
            :placeholder="$t('shared.component.select.select_or_type')"
            @on-change="handle_finance_change"
            @on-create="handle_finance_create"
          >
            <i-option v-for="item in form.finance_email" :key="item" :value="item">
              {{ item }}
            </i-option>
            <i-option v-for="item in valid_finance_email" :key="item" :value="item">
              {{ item }}
            </i-option>
          </i-select>
          <div v-if="errors.finance_email" class="form-input__warning form-input__error--absolute">
            {{ $t('core.payment.errors.email') }}
          </div>
        </div>
      </div>

      <z-form-group class="row">
        <div class="col-3 text-right">{{ $t('payment.email_rates') }}</div>
        <div class="col-9">
          <i-select
            v-model="new_rates_emails"
            :multiple="true"
            :filterable="true"
            :allow-create="true"
            :disabled="is_rates_loading"
            :placeholder="$t('shared.component.select.select_or_type')"
            @on-change="handle_rates_change"
            @on-create="handle_rates_create"
          >
            <i-option v-for="item in form.email_rates" :key="item" :value="item">
              {{ item }}
            </i-option>
            <i-option v-for="item in valid_rates_email" :key="item" :value="item">
              {{ item }}
            </i-option>
          </i-select>
          <div v-if="errors.rates_email" class="form-input__warning form-input__error--absolute">
            {{ $t('core.payment.errors.email') }}
          </div>
        </div>
      </z-form-group>

      <z-form-group class="row">
        <div class="col-3 text-right">
          {{ $t('core.profile.form.field.timezone.title') }}
        </div>

        <z-form-input class="col-9" vid="timezone">
          <time-zone-select v-model="form.timezone" />
        </z-form-input>
      </z-form-group>

      <z-form-group class="row">
        <div class="col-3 text-right">
          {{ $t('core.profile.form.field.phone.title') }}
        </div>
        <z-form-input class="col-9" vid="phone">
          <i-input v-model="form.phone" />
        </z-form-input>
      </z-form-group>

      <z-form-group class="row">
        <div class="col-3 text-right">
          {{ $t('core.profile.form.field.name.title') }}
        </div>
        <z-form-input class="col-9" :rules="{ required: true }" vid="name">
          <i-input v-model="form.name" />
        </z-form-input>
      </z-form-group>

      <z-form-group class="row">
        <div class="col-3 text-right">
          {{ $t('core.profile.form.field.company.title') }}
        </div>
        <z-form-input class="col-9" vid="company">
          <i-input v-model="form.company" />
        </z-form-input>
      </z-form-group>

      <div class="row">
        <div class="col-12 text-right">
          <i-button type="default" html-type="submit" size="large" :loading="is_loading">
            {{ $t('shared.component.button.update') }}
          </i-button>
        </div>
      </div>
    </div>
  </z-form>
</template>

<script>
  import { PageServerValidationResponseMixin } from '@shared/mixins/page-server-validation-response';
  import { mapGetters, mapActions } from 'vuex';
  import pick from 'lodash-es/pick';
  import defaultsDeep from 'lodash-es/defaultsDeep';
  import TimeZoneSelect from '@shared/components/input/input-timezone-select';

  // eslint-disable-next-line no-useless-escape
  const emailRegExp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  export default {
    components: { TimeZoneSelect },
    mixins: [PageServerValidationResponseMixin],
    data: () => ({
      form: {},
      is_loading: false,
      new_finance_emails: [],
      new_rates_emails: [],
      is_finance_loading: false,
      is_rates_loading: false,
      valid_finance_email: [],
      valid_rates_email: [],
      errors: {
        finance_email: false,
        rates_email: false
      }
    }),

    computed: {
      ...mapGetters('core/auth', ['user'])
    },

    created() {
      this.new_finance_emails = this.form.finance_email;
      this.new_rates_emails = this.form.email_rates;
    },

    watch: {
      user: {
        handler: function (val) {
          this.form = defaultsDeep(
            pick(val, [
              'email',
              'name',
              'company',
              'phone',
              'timezone',
              'finance_email',
              'email_rates'
            ]),
            {
              email: '',
              name: '',
              company: '',
              phone: '',
              timezone: null,
              finance_email: [],
              email_rates: []
            }
          );
        },
        immediate: true,
        deep: true
      }
    },

    methods: {
      ...mapActions('core/auth', ['setProfileBackendWithStore']),

      handle_finance_create(value) {
        if (emailRegExp.test(value)) {
          this.valid_finance_email.push(value);
        }
      },

      handle_finance_change(array) {
        if (this.new_finance_emails.filter((i) => !emailRegExp.test(i)).length > 0) {
          this.errors.finance_email = true;
        }
        const valid_emails = array.filter((i) => emailRegExp.test(i));
        this.new_finance_emails = valid_emails;
      },

      handle_rates_create(value) {
        if (emailRegExp.test(value)) {
          this.valid_rates_email.push(value);
        }
      },

      handle_rates_change(array) {
        if (this.new_rates_emails.filter((i) => !emailRegExp.test(i)).length > 0) {
          this.errors.rates_email = true;
        }
        const valid_emails = array.filter((i) => emailRegExp.test(i));
        this.new_rates_emails = valid_emails;
      },

      submit: function () {
        this.$refs.form.validate().then((is_valid) => {
          if (is_valid) {
            this.is_loading = true;
            this.errors = {
              new_finance_emails: false,
              rates_email: false
            };

            this.setProfileBackendWithStore({
              ...this.form,
              finance_email: this.new_finance_emails,
              email_rates: this.new_rates_emails
            })
              .catch((err) => this.check_server_validation(err))
              .finally(() => (this.is_loading = false));
          }
        });
      }
    }
  };
</script>